import { Box, Dialog, IconButton, Link } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { POPUP_SEEN_KEY } from "../../constants/popup-constants";
import { PopupSettings } from "../../types/Settings";
import * as styles from "./popupStyles";

// The decision (closed or clicked) is remembered per image for the lifetime of
// the tab, so a newly uploaded popup image is shown again.
function hasSeenPopup(imageId: string): boolean {
    try {
        return sessionStorage.getItem(POPUP_SEEN_KEY) === imageId;
    } catch {
        return false;
    }
}

function markPopupSeen(imageId: string) {
    try {
        sessionStorage.setItem(POPUP_SEEN_KEY, imageId);
    } catch {
        // storage blocked: the popup can show again on the next page load
    }
}

interface PopupProps {
    popup: PopupSettings;
}

const Popup: React.FC<PopupProps> = ({ popup }) => {
    const [open, setOpen] = useState(false);
    const isEnabled = popup.show && Boolean(popup.imageUrl);

    useEffect(() => {
        if (!isEnabled || hasSeenPopup(popup.imageId)) {
            return;
        }

        const timer = setTimeout(() => setOpen(true), popup.delaySeconds * 1000);
        return () => clearTimeout(timer);
    }, [isEnabled, popup.imageId, popup.delaySeconds]);

    const handleClose = () => {
        markPopupSeen(popup.imageId);
        setOpen(false);
    };

    const handleImageClick = () => {
        if (typeof window !== "undefined" && (window as any)._paq) {
            (window as any)._paq.push(["trackLink", popup.url, "link"]);
        }
        markPopupSeen(popup.imageId);
    };

    const image = (
        <Box
            component="img"
            sx={styles.popupImage}
            alt={popup.alt}
            src={popup.imageUrl}
        />
    );

    return (
        <Dialog open={open} onClose={handleClose} PaperProps={{ sx: styles.popupPaper }}>
            <IconButton
                aria-label="Popup schließen"
                onClick={handleClose}
                sx={styles.closeButton}
            >
                <CloseIcon />
            </IconButton>

            {popup.url ? (
                <Link href={popup.url} onClick={handleImageClick}>
                    {image}
                </Link>
            ) : (
                image
            )}
        </Dialog>
    );
};

export default Popup;
